import { msg } from '@lingui/macro';
import type { I18n, MessageDescriptor } from '@lingui/core';
import type { Service } from '@/types/billing';

/**
 * Get localized service name from billing settings
 * Looks up the service by code and returns the name in the current locale
 * Falls back to name_en or the base name field if locale not available
 */
export function getLocalizedServiceName(
  serviceCode: string,
  services: Service[],
  locale: string
): string {
  const service = services.find((s) => s.code === serviceCode);
  if (!service) {
    return serviceCode; // Fallback to code if service not found
  }

  // Map locale to service name field
  const localeFieldMap: Record<string, keyof Service> = {
    de: 'name_de',
    en: 'name_en',
    es: 'name_es',
    fr: 'name_fr',
    it: 'name_it',
    pt: 'name_pt',
  };

  const field = localeFieldMap[locale];
  const localizedName = field ? service[field] : undefined;

  // Fallback chain: requested locale -> name_en -> name -> code
  return (
    (localizedName as string | undefined) ||
    service.name_en ||
    service.name ||
    serviceCode
  );
}

/**
 * Translates a service code to a human-readable label using billing settings
 */
export function translateService(serviceCode: string, services: Service[], i18n: I18n): string {
  return getLocalizedServiceName(serviceCode, services, i18n.locale);
}

/**
 * Weekday labels, 1 = Monday .. 7 = Sunday.
 *
 * These are `msg()` descriptors, not plain objects: the Lingui extractor reads
 * the source statically, so a bare `{ id, message }` literal passed through a
 * variable is never extracted — the ids stayed out of every catalog and each
 * weekday rendered in English in all five translated locales.
 */
const WEEKDAY_LABELS: Record<number, MessageDescriptor> = {
  1: msg({ id: 'capacity.weekday.monday', message: 'Monday' }),
  2: msg({ id: 'capacity.weekday.tuesday', message: 'Tuesday' }),
  3: msg({ id: 'capacity.weekday.wednesday', message: 'Wednesday' }),
  4: msg({ id: 'capacity.weekday.thursday', message: 'Thursday' }),
  5: msg({ id: 'capacity.weekday.friday', message: 'Friday' }),
  6: msg({ id: 'capacity.weekday.saturday', message: 'Saturday' }),
  7: msg({ id: 'capacity.weekday.sunday', message: 'Sunday' }),
};

/**
 * Translates a weekday number (1-7, Monday-Sunday) to a human-readable label
 */
export function translateWeekday(weekday: number, i18n: I18n): string {
  const label = WEEKDAY_LABELS[weekday];
  return label ? i18n._(label) : `Weekday ${weekday}`;
}

/**
 * Formats a service code array as a human-readable list
 * Example: ['FULL_DAY', 'LATE'] => "Full Day, Late" (or localized equivalents)
 */
export function formatServiceList(serviceCodes: string[], services: Service[], i18n: I18n): string {
  return serviceCodes.map((code) => translateService(code, services, i18n)).join(', ');
}

/**
 * Formats a weekday-number array as a human-readable list.
 * Example: [1, 5] => "Monday and Friday" (or the localized equivalent)
 */
export function formatWeekdayList(weekdays: number[], i18n: I18n): string {
  const names = weekdays.map((weekday) => translateWeekday(weekday, i18n));
  if (names.length <= 1) {
    return names[0] ?? '';
  }

  return i18n._(
    { id: 'capacity.weekday.listJoin', message: '{head} and {last}' },
    { head: names.slice(0, -1).join(', '), last: names[names.length - 1] }
  );
}
